import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "briefschrift_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);
const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const client = axios.create({ baseURL: API });
client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearToken();
      if (!window.location.pathname.startsWith("/login")) window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export const api = {
  auth: {
    register: (payload) => client.post("/auth/register", payload).then((r) => r.data),
    login: (payload) => client.post("/auth/login", payload).then((r) => r.data),
    me: () => client.get("/auth/me").then((r) => r.data),
    logout: () => client.post("/auth/logout").then((r) => r.data),
    changePassword: (payload) => client.post("/auth/change-password", payload).then((r) => r.data),
    forgotPassword: (email) => client.post("/auth/forgot-password", { email }).then((r) => r.data),
    resetPassword: (payload) => client.post("/auth/reset-password", payload).then((r) => r.data),
  },
  admin: {
    listUsers: () => client.get("/admin/users").then((r) => r.data),
    approve: (id) => client.post(`/admin/users/${id}/approve`).then((r) => r.data),
    reject: (id) => client.post(`/admin/users/${id}/reject`).then((r) => r.data),
    deleteUser: (id) => client.delete(`/admin/users/${id}`).then((r) => r.data),
  },

  letters: {
    list: () => client.get("/letters").then((r) => r.data),
    suggestions: (letterId) => client.get(`/letters/${letterId}/suggestions`).then((r) => r.data),
    addSuggestion: (letterId, payload) => client.post(`/letters/${letterId}/suggestions`, payload).then((r) => r.data),
    applySuggestion: (letterId, sid) => client.post(`/letters/${letterId}/suggestions/${sid}/apply`).then((r) => r.data),
    deleteSuggestion: (letterId, sid) => client.delete(`/letters/${letterId}/suggestions/${sid}`).then((r) => r.data),
    editSuggestions: (letterId) => client.get(`/letters/${letterId}/edit-suggestions`).then((r) => r.data),
    addEditSuggestion: (letterId, payload) => client.post(`/letters/${letterId}/edit-suggestions`, payload).then((r) => r.data),
    approveEditSuggestion: (letterId, sid) => client.post(`/letters/${letterId}/edit-suggestions/${sid}/approve`).then((r) => r.data),
    rejectEditSuggestion: (letterId, sid) => client.delete(`/letters/${letterId}/edit-suggestions/${sid}`).then((r) => r.data),
    updateTranscription: (letterId, transcription) => client.put(`/letters/${letterId}/meta`, { transcription }).then((r) => r.data),
    summarize: (letterId) => client.post(`/letters/${letterId}/summarize`).then((r) => r.data),
    generateHighlights: (letterId) => client.post(`/letters/${letterId}/highlights`).then((r) => r.data),
    generateQuote: (letterId) => client.post(`/letters/${letterId}/quote`).then((r) => r.data),
    generateQuotesBatch: (ids) => client.post(`/letters/quotes/batch`, { ids }).then((r) => r.data),
  },

  transcribe: (payload) => client.post("/transcribe", payload).then((r) => r.data),
  convertPdf: (pdf_base64) => client.post("/convert-pdf", { pdf_base64 }).then((r) => r.data),
  fetchImage: (url) => client.post("/fetch-image", { url }).then((r) => r.data),

  listLetters: ({ images = false, thumbnails = false } = {}) => {
    const params = {};
    if (images) params.include_images = true;
    if (thumbnails) params.include_thumbnails = true;
    return client.get("/letters", { params }).then((r) => r.data);
  },
  getStory: () => client.get("/story").then((r) => r.data),
  generateStory: () => client.post("/story/generate").then((r) => r.data),
  getBookChapters: () => client.get("/book/chapters").then((r) => r.data),
  getBookCover: () => client.get("/settings/book-cover").then((r) => r.data),
  setBookCover: (cover) => client.put("/settings/book-cover", { cover }).then((r) => r.data),
  getBookIntro: () => client.get("/settings/book-intro").then((r) => r.data),
  setBookIntro: (intro) => client.put("/settings/book-intro", { intro }).then((r) => r.data),
  generateBookIntro: () => client.post("/settings/book-intro/generate").then((r) => r.data),
  getBookObservations: () => client.get("/book/observations").then((r) => r.data),
  generateBookObservations: () => client.post("/book/observations/generate").then((r) => r.data),
  updateBookObservation: (id, text) => client.put(`/book/observations/${id}`, { text }).then((r) => r.data),
  deleteBookObservation: (id) => client.delete(`/book/observations/${id}`).then((r) => r.data),
  getBookOverviews: () => client.get("/book/overviews").then((r) => r.data),
  generateBookOverviews: () => client.post("/book/overviews/generate").then((r) => r.data),
  updateBookOverview: (id, text) => client.put(`/book/overviews/${id}`, { text }).then((r) => r.data),
  deleteBookOverview: (id) => client.delete(`/book/overviews/${id}`).then((r) => r.data),
  generateChapterTitle: (year, month) => client.post("/book/chapter-title", { year, month }).then((r) => r.data),
  generateChapterTitles: (chapters) => client.post("/book/chapter-titles", { chapters }).then((r) => r.data),
  getLetter: (id) => client.get(`/letters/${id}`).then((r) => r.data),
  createLetter: (payload) => client.post("/letters", payload).then((r) => r.data),
  deleteLetter: (id) => client.delete(`/letters/${id}`).then((r) => r.data),
  linkLetter: (id, reply_to) => client.put(`/letters/${id}/link`, { reply_to }).then((r) => r.data),
  updateLetterTitle: (id, title) => client.put(`/letters/${id}/title`, { title }).then((r) => r.data),
  updateLetterMeta: (id, meta) => client.put(`/letters/${id}/meta`, meta).then((r) => r.data),
  updateLetterFront: (id, front_image) => client.put(`/letters/${id}/front_image`, { front_image }).then((r) => r.data),
  updateLetterImages: (id, images) => client.put(`/letters/${id}/images`, { images }).then((r) => r.data),

  listProfiles: () => client.get("/profiles").then((r) => r.data),
  createProfile: (payload) => client.post("/profiles", payload).then((r) => r.data),
  updateProfile: (id, payload) => client.put(`/profiles/${id}`, payload).then((r) => r.data),
  deleteProfile: (id) => client.delete(`/profiles/${id}`).then((r) => r.data),
  learnFromCorrection: (id, original, corrected) => client.post(`/profiles/${id}/learn`, { original, corrected }).then((r) => r.data),

  streamTranscribe: async (payload, { onDelta, onDone, onError }) => {
    const parseRaw = (raw) => {
      const [textPart, metaPart = ""] = raw.split("%%%META%%%");
      const out = { transcription: (textPart || "").trim(), uncertain: [], notes: "", title: "", date_text: "", date_iso: "", place: "", salutation: "", signature: "", summary: "", tone: "", is_typed: false, date_note: "", highlights: [] };
      if (metaPart.trim()) {
        try {
          const j = JSON.parse(metaPart.slice(metaPart.indexOf("{"), metaPart.lastIndexOf("}") + 1));
          Object.assign(out, { uncertain: j.uncertain || [], notes: j.notes || "", title: j.title || "", date_text: j.date_text || "", date_iso: j.date_iso || "", place: j.place || "", salutation: j.salutation || "", signature: j.signature || "", summary: j.summary || "", tone: j.tone || "", is_typed: !!j.is_typed, date_note: j.date_note || "", highlights: j.highlights || [] });
        } catch (e) { /* partial meta, keep transcription */ }
      }
      return out;
    };
    let raw = "";
    let finished = false;
    try {
      const res = await fetch(`${API}/transcribe/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok || !res.body) throw new Error("Streaming mislukt");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const chunk = buf.slice(0, idx).trim();
          buf = buf.slice(idx + 2);
          if (!chunk.startsWith("data:")) continue; // ignore ": keep-alive" comments
          const obj = JSON.parse(chunk.slice(5).trim());
          if (obj.error) { finished = true; return onError && onError(obj.error); }
          if (obj.done) { finished = true; return onDone && onDone(obj); }
          if (obj.delta !== undefined) {
            raw += obj.delta;
            onDelta && onDelta(raw);
          }
        }
      }
      // Stream ended without an explicit done event (e.g. connection dropped): salvage what we have.
      if (!finished) {
        if (raw.trim()) onDone && onDone(parseRaw(raw));
        else onError && onError("De verbinding werd verbroken voordat er tekst binnenkwam. Probeer het opnieuw, eventueel met minder of kleinere pagina's.");
      }
    } catch (e) {
      if (!finished) {
        if (raw.trim()) onDone && onDone(parseRaw(raw));
        else onError && onError(e.message);
      }
    }
  },

  // Poll-based transcription: start a background job, then poll status every ~2s.
  // Avoids the gateway's hard 60s connection cap (each poll is a short request).
  jobTranscribe: async (payload, { onPartial, onDone, onError }) => {
    try {
      const { job_id } = await client.post("/transcribe/start", payload).then((r) => r.data);
      if (!job_id) throw new Error("Kon transcriptie-taak niet starten");
      const deadline = Date.now() + 10 * 60 * 1000; // 10 min hard stop
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2000));
        let data;
        try {
          data = await client.get(`/transcribe/status/${job_id}`).then((r) => r.data);
        } catch (e) {
          if (e?.response?.status === 404) {
            return onError && onError("Taak verlopen. Probeer het opnieuw.");
          }
          continue; // transient network error: keep polling
        }
        if (data.status === "running") {
          if (data.partial) onPartial && onPartial(data.partial);
          continue;
        }
        if (data.status === "error") {
          return onError && onError(data.error || "Transcriptie mislukt");
        }
        if (data.status === "done") {
          return onDone && onDone(data);
        }
      }
      onError && onError("Transcriptie duurde te lang. Probeer het opnieuw met kleinere pagina's.");
    } catch (e) {
      onError && onError(e?.response?.data?.detail || e.message || "Transcriptie mislukt");
    }
  },

  exportFile: async (format, payload) => {
    const res = await fetch(`${API}/export/${format}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Export mislukt");
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="(.+?)"/);
    const name = m ? m[1] : `transcriptie.${format}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  },
};
